/*!
 * Consent tracking for Snowplow v4.8.0 ()
 * Copyright 2022 Snowplow Analytics Ltd, 2010 Anthon Pang
 * Licensed under BSD-3-Clause
 */

import { __assign, __rest } from 'tslib';
import { dispatchToTrackersInCollection } from '@snowplow/browser-tracker-core';
import { buildSelfDescribingEvent } from '@snowplow/tracker-core';

var CONSENT_SCHEMA = 'iglu:com.snowplowanalytics.snowplow/consent_preferences/jsonschema/1-0-0';
var CMP_VISIBLE_SCHEMA = 'iglu:com.snowplowanalytics.snowplow/cmp_visible/jsonschema/1-0-0';

/**
 * Build a Consent Action Event
 * for tracking consent actions
 *
 * @param event - Contains properties of Consent action events
 * @returns PayloadBuilder to be sent to {@link @snowplow/tracker-core#TrackerCore.track}
 */
function buildConsentEvent(event) {
    return buildSelfDescribingEvent({
        event: {
            schema: CONSENT_SCHEMA,
            data: removeEmptyProperties(__assign({}, event)),
        },
    });
}
/**
 * Build a CMP Visible Event
 * for tracking loadtime of CMP banner
 *
 * @param event - Contains properties of CMP Visible events
 * @returns PayloadBuilder to be sent to {@link @snowplow/tracker-core#TrackerCore.track}
 */
function buildCmpVisibleEvent(event) {
    return buildSelfDescribingEvent({
        event: {
            schema: CMP_VISIBLE_SCHEMA,
            data: removeEmptyProperties(__assign({}, event)),
        },
    });
}
/**
 * Returns a copy of a JSON with undefined and null properties removed
 *
 * @param event - Object to clean
 * @returns A cleaned copy of eventJson
 */
function removeEmptyProperties(event) {
    var ret = {};
    for (var k in event) {
        if (event[k] !== null && typeof event[k] !== 'undefined') {
            ret[k] = event[k];
        }
    }
    return ret;
}

var _trackers = {};
/**
 * Adds consent tracking
 */
function EnhancedConsentPlugin() {
    var trackerId;
    return {
        activateBrowserPlugin: function (tracker) {
            trackerId = tracker.id;
            _trackers[trackerId] = tracker;
        },
    };
}
/**
 * Track the loadtime of a CMP banner
 */
function trackCmpVisible(cmpVisible, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var context = cmpVisible.context, timestamp = cmpVisible.timestamp, elapsedTime = cmpVisible.elapsedTime;
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildCmpVisibleEvent({
            elapsedTime: elapsedTime,
        }), context, timestamp);
    });
}
// Generic consent action
function trackConsentAction(consent, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var context = consent.context, timestamp = consent.timestamp, consentAttributes = __rest(consent, ["context", "timestamp"]);
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildConsentEvent(consentAttributes), context, timestamp);
    });
}
/**
 * Track an allow consent event
 *
 * @param consent - The consent information
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackConsentAllow(consent, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    trackConsentAction(__assign(__assign({}, consent), { eventType: 'allow_all' }), trackers);
}
/**
 * Track an allow selected consent event
 *
 * @param consent - The consent information
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackConsentSelected(consent, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    trackConsentAction(__assign(__assign({}, consent), { eventType: 'allow_selected' }), trackers);
}
/**
 * Track a consent pending event
 *
 * @param consent - The consent information
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackConsentPending(consent, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    trackConsentAction(__assign(__assign({}, consent), { eventType: 'pending' }), trackers);
}
/**
 * Track an implicit consent event
 *
 * @param consent - The consent information
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackConsentImplicit(consent, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    trackConsentAction(__assign(__assign({}, consent), { eventType: 'implicit_consent' }), trackers);
}
/**
 * Track a deny consent event
 *
 * @param consent - The consent information
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackConsentDeny(consent, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    trackConsentAction(__assign(__assign({}, consent), { eventType: 'deny_all' }), trackers);
}
/**
 * Track a consent expired event
 *
 * @param consent - The consent information
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackConsentExpired(consent, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    trackConsentAction(__assign(__assign({}, consent), { eventType: 'expired' }), trackers);
}
/**
 * Track a consent withdrawn event
 *
 * @param consent - The consent information
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackConsentWithdrawn(consent, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    trackConsentAction(__assign(__assign({}, consent), { eventType: 'withdrawn' }), trackers);
}

export { EnhancedConsentPlugin, trackCmpVisible, trackConsentAllow, trackConsentDeny, trackConsentExpired, trackConsentImplicit, trackConsentPending, trackConsentSelected, trackConsentWithdrawn };
//# sourceMappingURL=index.module.js.map
